import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';

/**
 * Async confirm dialog that works inside cross-origin iframes (where native
 * window.confirm() is silently blocked / returns false).
 *
 * Usage:
 *   import { confirmDialog } from '../lib/confirm';
 *   if (!(await confirmDialog('Klant verwijderen?'))) return;
 */

let mountNode = null;
let root = null;

function ensureRoot() {
  if (mountNode) return;
  mountNode = document.createElement('div');
  mountNode.id = 'mc-confirm-root';
  document.body.appendChild(mountNode);
  root = ReactDOM.createRoot(mountNode);
}

function Dialog({ message, onResolve, confirmText = 'OK', cancelText = 'Annuleren', danger = true }) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const close = (val) => { setOpen(false); setTimeout(() => onResolve(val), 50); };
  if (!open) return null;
  return (
    <div
      onClick={() => close(false)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 99999, backdropFilter: 'blur(2px)',
      }}
      data-testid="confirm-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--cs-card, #fff)', color: 'var(--cs-text, #0f172a)',
          borderRadius: 10, padding: 22, minWidth: 320, maxWidth: 480,
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
        data-testid="confirm-dialog"
      >
        <div style={{ fontSize: 15, marginBottom: 18, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
          {message}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            className="cs-btn"
            onClick={() => close(false)}
            data-testid="confirm-cancel"
            autoFocus
          >
            {cancelText}
          </button>
          <button
            className={`cs-btn ${danger ? 'danger' : 'primary'}`}
            onClick={() => close(true)}
            data-testid="confirm-ok"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function confirmDialog(message, opts = {}) {
  ensureRoot();
  return new Promise((resolve) => {
    root.render(
      <Dialog
        message={message}
        confirmText={opts.confirmText}
        cancelText={opts.cancelText}
        danger={opts.danger !== false}
        onResolve={(val) => {
          root.render(null);
          resolve(val);
        }}
      />
    );
  });
}

// Drop-in shim for legacy `confirm(...)` calls that still return synchronously.
// Returns a Promise; callers that don't await will get truthy Promise.
// This is intentional so old `if (!confirm(...))` patterns still proceed,
// while migrated `if (!(await confirmDialog(...)))` patterns work properly.
export default confirmDialog;
